/**
 * Dashboard v1 (§9.1 item 10, NFR-3, S1).
 *
 * Success criterion S1 gives this screen five seconds to answer "where is the
 * farm's money?" on a phone, on 3G. Two things follow.
 *
 * The balances come first, above everything else, from a single aggregation
 * endpoint — the client sums nothing (MONEY-3).
 *
 * The chart is lazy. Recharts is about as large as the rest of the app put
 * together, so it lives in its own chunk and arrives after the numbers that
 * actually matter. A partner who only wanted the cash balance has already got
 * it by the time the pie renders.
 */

import { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatCompactBDT, formatMoney, isNegativeAmount } from '../../lib/currency.js';
import { useFinanceDashboard } from '../finance/api.js';
import { useAging, useBreakEven } from '../sales/api.js';

const ExpensePie = lazy(() => import('./ExpensePie.js'));

function Tile({ label, value, muted = false }: { label: string; value: string; muted?: boolean }): JSX.Element {
  return (
    <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-0.5 tabular-nums text-lg font-semibold ${muted ? 'text-gray-600' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  );
}

export function DashboardV1(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useFinanceDashboard();
  // v2 additions. Loaded alongside rather than blocking: the balances are what
  // S1 promises in five seconds, and these are the second question.
  const { data: agingData } = useAging();
  const { data: breakEvenRaw } = useBreakEven();

  /*
   * Narrowed on the fields actually read below, not merely on the query having
   * resolved.
   *
   * These two tiles are decoration on the screen S1 gives five seconds to
   * answer "where is the farm's money?". A payload that arrives in an
   * unexpected shape — an older server, a partial response, a proxy that
   * rewrote the body — must cost the farm one tile, never the balances. Reading
   * `aging.totals.total` behind a bare `!== undefined` check throws during
   * render and blanks the whole dashboard instead.
   */
  const aging =
    typeof agingData?.totals?.total === 'string' && typeof agingData.totals.over60 === 'string' ? agingData : undefined;

  const breakEvenData =
    typeof breakEvenRaw?.percent === 'string' && typeof breakEvenRaw.remaining === 'string' ? breakEvenRaw : undefined;

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('dashboard.title')}</h1>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {data && (
        <>
          {/* S1: the three balances, first, before anything else renders. */}
          <section className="mb-4 md:grid md:grid-cols-4 md:gap-3" aria-label={t('dashboard.balances')}>
            <div className="mb-2 rounded-xl bg-green-700 px-4 py-4 text-white md:mb-0">
              <p className="text-xs opacity-90">{t('dashboard.onHand')}</p>
              <p className="mt-0.5 text-3xl font-semibold tabular-nums">{formatMoney(data.totalOnHand)}</p>
            </div>

            <ul className="grid grid-cols-3 gap-2 md:col-span-3 md:gap-3">
              {data.accounts.map((account) => (
                <li key={account.id} className="rounded-xl bg-white px-3 py-3 shadow-sm">
                  <p className="truncate text-xs text-gray-500">{account.name}</p>
                  <p
                    className={`mt-0.5 tabular-nums text-sm font-semibold ${
                      isNegativeAmount(account.balance) ? 'text-red-700' : 'text-gray-900'
                    }`}
                  >
                    {formatMoney(account.balance, { decimals: 'never' })}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          {data.pendingApprovals > 0 && (
            // The badge is what makes maker-checker happen. Without it an entry
            // can sit pending for a week and nobody is at fault.
            <Link
              to="/money"
              className="mb-4 flex min-h-touch items-center justify-between rounded-xl bg-amber-50 px-4 py-3 text-amber-900"
            >
              <span className="text-sm">{t('dashboard.pending', { count: data.pendingApprovals })}</span>
              <span aria-hidden="true">→</span>
            </Link>
          )}

          {/* Two across on a phone, five on a desktop: the KPI row an admin
              panel is expected to open with. */}
          <section className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-3" aria-label={t('dashboard.summary')}>
            <Tile label={t('dashboard.capital')} value={formatCompactBDT(data.totalCapital)} />
            <Tile label={t('dashboard.spent')} value={formatCompactBDT(data.totalSpent)} />
            <Tile label={t('dashboard.thisMonth')} value={formatMoney(data.thisMonthExpense, { decimals: 'never' })} />
            <Tile
              label={t('dashboard.payables')}
              value={formatMoney(data.totalPayables, { decimals: 'never' })}
              muted={data.totalPayables === '0.00'}
            />
            {/* v2 — what the farm is owed sits next to what it owes, because
                one is only meaningful beside the other (BR-24). */}
            {aging !== undefined && (
              <Tile
                label={t('dashboard.receivables')}
                value={formatMoney(aging.totals.total, { decimals: 'never' })}
                muted={aging.totals.total === '0.00'}
              />
            )}
          </section>

          {/* v2 — the figure the partners care about most: how much of the
              ৳6,00,000 has come back (§11.1). */}
          {breakEvenData !== undefined && (
            <section className="mb-4 rounded-xl bg-white p-4 shadow-sm" aria-label={t('dashboard.breakEven')}>
              <div className="flex items-baseline justify-between">
                <h2 className="text-sm font-medium text-gray-700">{t('dashboard.breakEven')}</h2>
                <span className="text-sm tabular-nums text-gray-600">{breakEvenData.percent}%</span>
              </div>

              <div className="mt-2 h-3 overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full ${breakEvenData.isRecovered ? 'bg-green-600' : 'bg-amber-500'}`}
                  style={{ width: `${breakEvenData.percent}%` }}
                />
              </div>

              <p className="mt-2 text-xs text-gray-500">
                {breakEvenData.isRecovered
                  ? t('dashboard.breakEvenDone')
                  : t('dashboard.breakEvenLeft', { amount: formatMoney(breakEvenData.remaining) })}
              </p>
            </section>
          )}

          {/* v2 — overdue debt is the one receivables figure worth interrupting
              somebody about. */}
          {aging !== undefined && aging.totals.over60 !== '0.00' && (
            <Link
              to="/receivables"
              className="mb-4 flex min-h-touch items-center justify-between rounded-xl bg-red-50 px-4 py-3 text-red-900"
            >
              <span className="text-sm">{t('dashboard.overdue', { amount: formatMoney(aging.totals.over60) })}</span>
              <span aria-hidden="true">→</span>
            </Link>
          )}

          <section className="rounded-xl bg-white p-4 shadow-sm md:mt-0" aria-label={t('dashboard.byCategory')}>
            <h2 className="mb-2 text-sm font-medium text-gray-700">{t('dashboard.byCategory')}</h2>
            <Suspense fallback={<p className="py-6 text-center text-sm text-gray-400">{t('app.loading')}</p>}>
              <ExpensePie data={data.thisMonthByCategory} />
            </Suspense>
          </section>

          {/* The quick links are the phone's way to the rest of the app; the
              sidebar already does this on desktop, so they go away there. */}
          <nav className="mt-4 grid grid-cols-2 gap-2 md:hidden" aria-label={t('dashboard.more')}>
            {[
              { to: '/books', key: 'finance.books.title' },
              { to: '/partners', key: 'finance.partners.title' },
              { to: '/vendors', key: 'finance.vendors.title' },
              { to: '/assets', key: 'finance.assets.title' },
              { to: '/reports', key: 'reports.title' },
              { to: '/receivables', key: 'receivables.title' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex min-h-touch items-center rounded-xl bg-white px-4 py-3 text-sm text-gray-700 shadow-sm"
              >
                {t(link.key)}
              </Link>
            ))}
          </nav>
        </>
      )}
    </div>
  );
}
